import { all, call, put, select, take, takeEvery } from 'redux-saga/effects'
import { t } from 'ttag'
import api from '../../services/api.services'
import { ActivityType, OnboardingStatus, ServiceProvider } from '../../services/api.types'
import { getWhoAmI, getWhoAmIFail, getWhoAmISuccess } from '../auth/authActions'
import { openUrl } from '../helpers/routeHelpers'
import { addNotification } from '../notifications/NotificationActions'
import { INotification } from '../notifications/NotificationReducers'
import { ROUTES } from '../Routes/routePaths'
import { push } from 'connected-react-router'
import {
  appleHealthIntegration,
  endIntegration,
  finalizeGarminIntegration,
  initialAppleHealthIntegration,
  initializeGarminIntegration,
  setIntegration,
  setIntegrationSuccess,
  syncIntegration,
} from './integrationActions'
import { delay } from 'q'
import { getAuthData } from '../../views/RegisterUser/store/RegisterUserSelectors'
import { dataSync } from '../../services/DataSync'
import { Capacitor } from '@capacitor/core'
import { log } from '../../services/log'
import { getErrorMsg } from '../helpers/translations'
import { GoogleFit } from '../../services/GoogleFit'
import { isNative } from '../helpers/isNative'
import { Preferences } from '@capacitor/preferences'
import { appMetaData } from '../auth/authSagas'

const CommonDataTypes = [
  { dataType: 'steps', activityType: ActivityType.STEPS, aggregated: true, fullScope: false },
  { dataType: 'distance', activityType: ActivityType.DISTANCE, aggregated: true, fullScope: false },
  { dataType: 'calories', activityType: ActivityType.CALORIES, aggregated: true, fullScope: true },
  { dataType: 'heart_rate', activityType: ActivityType.HEART_RATE, aggregated: false, fullScope: true },
]

export const AppleHealthDataTypes = [
  ...CommonDataTypes,
  {
    dataType: 'activity',
    activityType: null,
    aggregated: false,
    fullScope: false,
  },
  {
    dataType: 'appleExerciseTime',
    activityType: ActivityType.EXERCISE_MIN,
    aggregated: true,
    fullScope: false,
  },
  {
    dataType: 'sleepMinutes',
    activityType: ActivityType.SLEEP_MIN,
    aggregated: true,
    fullScope: false,
  },
]

export const HealthConnectDataTypes = [
  ...CommonDataTypes,
  {
    dataType: 'activity',
    activityType: null,
    aggregated: false,
    fullScope: false,
  },
  {
    dataType: 'sleep',
    activityType: ActivityType.SLEEP_MIN,
    aggregated: false,
    fullScope: false,
  },
]

export const GoogleFitDataTypes = [
  ...CommonDataTypes,
  {
    dataType: 'move_minutes',
    activityType: ActivityType.EXERCISE_MIN,
    aggregated: true,
    fullScope: false,
  },
]

export function* sagaSetIntegration({ payload }: any) {
  try {
    const { serviceProvider, redirectUrl } = payload
    // In some cases, authorizationCode comes back encoded.
    const authorizationCode = decodeURIComponent(payload.authorizationCode)
    yield call(api.activity.setUserThirdPartyIntegration, {
      authorizationCode,
      redirectUrl,
      serviceProvider,
    })
    const timer = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

    for (const turn of timer) {
      yield delay(500)
      const {
        userDto: { onboardingStatus },
      } = yield call(api.auth.getWhoAmI, appMetaData)

      if (turn === 10 || onboardingStatus !== OnboardingStatus.INTEGRATION) {
        yield Preferences.remove({ key: 'isIntegrationHost' })
        yield put({ type: String(getWhoAmI) })
        yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
        //yield put({ type: String(reLocate) })
        yield put({ type: String(setIntegrationSuccess) })

        // TODO: Maybe we should prompt this before the integration is done?
        if (Capacitor.getPlatform() === 'android' && serviceProvider === ServiceProvider.GOOGLE_FIT) {
          // Before syncing, make sure Google Fit app is installed (you're welcome Claus).
          console.log('promptInstallFit check')
          yield GoogleFit.promptInstallFit()
            .then(() => {
              console.log('promptInstallFit success')
              dataSync(true, true)
            })
            .catch((error: any) => {
              console.error('promptInstallFit error', error)
              log(`promptInstallFit error: ${JSON.stringify(error)}`)
            })
        }

        break
      }
    }
  } catch (e: any) {
    const { onboardingStatus } = yield select(getAuthData)

    let content
    if (e.status === 400 && e.message === 'Integration account is in used by another YuMuuv user!') {
      content = t`We’re sorry but this Polar account is already integrated with another YuMuuv account. If needed, contact our support support@yumuuv.com`
    } else {
      content = getErrorMsg('third party error')
    }

    const to = onboardingStatus === OnboardingStatus.INTEGRATION ? ROUTES.REGISTER_USER.to : ROUTES.SETTINGS.to
    const notification: INotification = { content, type: 'warning' }
    yield put(push(to, { notification }))
  }
}

export function* sagaSyncIntegration({ payload }: any) {
  try {
    yield call(api.activity.syncUserThirdPartyData)
    //const response = yield call(api.activity.syncUserThirdPartyData)
    //yield put({ type: String(syncIntegrationSuccess), payload: response })

    if (!payload || !payload.noMessage) {
      const toggleSuccessNotification: INotification = { content: t`Sync has been initiated!`, type: 'success' }
      yield put({ type: String(addNotification), payload: toggleSuccessNotification })
    }
  } catch (e: any) {
    /*const content = t`There was a problem within our system while updating. Please refresh or contact our support support@yumuuv.com`
    const toggleErrorNotification: INotification = { content, type: 'warning' }

    yield put({ type: String(addNotification), payload: toggleErrorNotification })*/
  }
}

export function* sagaEndIntegration({ payload }: any) {
  try {
    yield call(api.activity.endUserThirdPartyIntegration)

    if (!payload || !payload.noMessage) {
      const toggleSuccessNotification: INotification = {
        content: t`Integration authorization has been cancelled!`,
        type: 'success',
      }
      yield put({ type: String(addNotification), payload: toggleSuccessNotification })
    }
  } catch (e: any) {
    /*const content = t`There was a problem within our system while updating. Please refresh or contact our support support@yumuuv.com`
    const toggleErrorNotification: INotification = { content, type: 'warning' }

    yield put({ type: String(addNotification), payload: toggleErrorNotification })*/
  }
}

function* sagaSyncAppleHealth() {
  if (isNative()) {
    yield dataSync(true, true)

    // Update userDto...
    yield put({ type: String(getWhoAmI) })
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
  }
}

function* sagaAppleHealthIntegration() {
  try {
    yield call(api.activity.setAppleHealthIntegration)
    const toggleSuccessNotification: INotification = { content: t`Integration success!`, type: 'success' }
    yield put({ type: String(addNotification), payload: toggleSuccessNotification })
    yield call(sagaSyncAppleHealth)
  } catch (e: any) {
    console.log('Error: ', e)
  }
}

export function* sagaInitializeGarminIntegration() {
  try {
    const redirectUrl = `${window.location.origin}${ROUTES.SETTINGS.to}`
    const { redirectView } = yield call(api.activity.saveGarminOauthRequestToken, { redirectUrl })
    yield put({ type: String(getWhoAmI) })
    openUrl(redirectView.url)
  } catch (e: any) {
    const toggleErrorNotification: INotification = {
      content: getErrorMsg('garmin init error'),
      type: 'warning',
    }
    yield put({ type: String(addNotification), payload: toggleErrorNotification })
  }
}

export const getUserDto = (state: any) => state.auth.userDto
export function* sagaFinalizeGarminIntegration({ payload }: any) {
  try {
    const { redirectUrl, oauthToken, oauthVerifierValue } = payload
    yield call(api.activity.finalizeGarminIntegration, {
      redirectUrl,
      oauthToken,
      oauthVerifierValue,
    })

    const timer = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

    for (const turn of timer) {
      yield delay(500)
      const { onboardingStatus } = yield call(api.auth.getWhoAmI, appMetaData)

      if (turn === 10 || onboardingStatus !== OnboardingStatus.INTEGRATION) {
        yield Preferences.remove({ key: 'isIntegrationHost' })
        yield put({ type: String(getWhoAmI) })
        yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
        //yield put({ type: String(reLocate) })
        yield put({ type: String(setIntegrationSuccess) })
        break
      }
    }
  } catch (e: any) {
    const { onboardingStatus } = yield select(getAuthData)

    let content
    if (e.status === 400 && e.message === 'Integration account is in used by another YuMuuv user!') {
      content = t`We’re sorry but this Garmin account is already integrated with another YuMuuv account. If needed, contact our support support@yumuuv.com`
    } else {
      content = getErrorMsg('garmin final error')
    }

    const to = onboardingStatus === OnboardingStatus.INTEGRATION ? ROUTES.REGISTER_USER.to : ROUTES.SETTINGS.to
    const notification: INotification = { content, type: 'warning' }
    yield put(push(to, { notification }))
  }
}

export function* sagaSetIntegrationSuccess() {
  const notification: INotification = {
    type: 'success',
    content: t`Integration success!`,
  }
  yield put(push(ROUTES.CLIENT_ACTIVITY.to, { notification }))
}

export function* integrationSagas() {
  yield all([
    takeEvery(String(setIntegration), sagaSetIntegration),
    takeEvery(String(endIntegration), sagaEndIntegration),
    takeEvery(String(syncIntegration), sagaSyncIntegration),
    takeEvery(String(initializeGarminIntegration), sagaInitializeGarminIntegration),
    takeEvery(String(finalizeGarminIntegration), sagaFinalizeGarminIntegration),
    takeEvery(String(initialAppleHealthIntegration), sagaSyncAppleHealth),
    takeEvery(String(appleHealthIntegration), sagaAppleHealthIntegration),
    takeEvery(String(setIntegrationSuccess), sagaSetIntegrationSuccess),
  ])
}
